import { gql } from '@apollo/client'
import styled from 'styled-components'

import { USER_PROJECT_CONTEXT } from 'shared/common/enums'
import { useSession } from 'shared/contexts/session-provider/session-provider'
import { isString, Nullable } from 'shared/utils/type-guards'

import {
  AvatarListUserFragment,
  ProjectTypeEnumType,
  useTeamCardProjectTeamQuery as useTeamQuery,
} from '~/common/types/graphql'
import { CardBody, CardHeader } from '~/views/components/elements/overview'
import AvatarList from '~/views/components/Overview/AvatarList'
import Card from '~/views/components/ui/Card'
import Text from '~/views/components/ui/Text'
import { useStaticLabels } from 'shared/hooks/use-static-labels'

const StyledCardBody = styled(CardBody)`
  display: grid;
  grid-template-columns: auto auto auto;
  gird-gap: ${({ theme }) => theme.spacing(3)};
`

const TeamLabel = styled(Text)(
  ({ theme }) => `
  margin-bottom: ${
    theme.name === 'lightV2' ? theme.spacing(2) : theme.spacing(3)
  }px;
  display: block;
`,
)

const TeamCell = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

export interface Props {
  className?: string
  project?: {
    id: string
    displayOrganization?: Nullable<{ name: string }>
    customer?: Nullable<{ name: string }>
    projectType?: Nullable<ProjectTypeEnumType>
  }
}

const TeamCard = ({ className, project }: Props) => {
  const { session } = useSession()
  const [getCommonLabel] = useStaticLabels(['common'])
  const shouldSkip = !session?.accessToken || !project?.id

  const res = useTeamQuery({
    variables: { projectId: project?.id || '' },
    skip: shouldSkip,
  })

  if (res.loading || res.error) return null

  const users = res.data?.projectTeam || []
  const team = users.reduce(
    (memo, user) => {
      if (!user) return memo
      if (!isString(user.projectContext)) return memo

      const context = user.projectContext

      if (memo[context]) {
        memo[context].push(user)
      } else {
        memo.partner.push(user)
      }

      return memo
    },
    {
      [USER_PROJECT_CONTEXT.provider]: [] as AvatarListUserFragment[],
      [USER_PROJECT_CONTEXT.customer]: [] as AvatarListUserFragment[],
      partner: [] as AvatarListUserFragment[],
    },
  )

  const customer = project?.customer

  return (
    <Card className={className}>
      <CardHeader>
        <Text size="large" weight="bold">
          {getCommonLabel('team')}
        </Text>
      </CardHeader>

      <StyledCardBody>
        <TeamCell>
          <TeamLabel color="mediumGray">
            {project?.displayOrganization?.name}
          </TeamLabel>
          <AvatarList users={team[USER_PROJECT_CONTEXT.provider]} />
        </TeamCell>

        {project?.projectType !== ProjectTypeEnumType.Internal && (
          <TeamCell>
            <TeamLabel color="mediumGray">{customer?.name}</TeamLabel>
            <AvatarList users={team[USER_PROJECT_CONTEXT.customer]} />
          </TeamCell>
        )}

        {team.partner.length > 0 && (
          <TeamCell>
            <TeamLabel color="mediumGray">
              {getCommonLabel('thirdParty')}
            </TeamLabel>
            <AvatarList users={team.partner} />
          </TeamCell>
        )}
      </StyledCardBody>
    </Card>
  )
}

// eslint-disable-next-line  no-unused-expressions
gql`
  query TeamCard_ProjectTeam($projectId: ID!) {
    projectTeam(projectId: $projectId) {
      id
      firstName
      lastName
      email
      jobRole
      avatar {
        originalUrl
      }
      projectContext
    }
  }
`

export default TeamCard
